import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertFeed = ({ alerts = [], onAlertClick, onMarkAsRead, onFilterChange }) => {
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const severityOptions = [
    { value: 'all', label: 'All Alerts' },
    { value: 'critical', label: 'Critical' },
    { value: 'warning', label: 'Warning' },
    { value: 'info', label: 'Info' }
  ];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'fleet', label: 'Fleet' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'safety', label: 'Safety' },
    { value: 'energy', label: 'Energy' }
  ];

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return 'AlertTriangle';
      case 'warning': return 'AlertCircle';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-error';
      case 'warning': return 'text-warning';
      case 'info': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-error/10';
      case 'warning': return 'bg-warning/10';
      case 'info': return 'bg-primary/10';
      default: return 'bg-muted/10';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'fleet': return 'Truck';
      case 'warehouse': return 'Package';
      case 'equipment': return 'Settings';
      case 'safety': return 'Shield';
      case 'energy': return 'Zap';
      default: return 'Bell';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diff = Math.floor((now - alertTime) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return alertTime?.toLocaleDateString();
  };

  const filteredAlerts = alerts?.filter(alert => {
    const severityMatch = selectedSeverity === 'all' || alert?.severity === selectedSeverity;
    const categoryMatch = selectedCategory === 'all' || alert?.category === selectedCategory;
    return severityMatch && categoryMatch;
  });

  const handleSeverityChange = (severity) => {
    setSelectedSeverity(severity);
    onFilterChange && onFilterChange({ severity, category: selectedCategory });
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    onFilterChange && onFilterChange({ severity: selectedSeverity, category });
  };

  return (
    <div className="bg-card border border-border rounded-lg h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Live Alerts</h3>
          <div className="flex items-center space-x-2">
            <div className="status-indicator status-online"></div>
            <span className="text-xs text-muted-foreground">Live</span>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Severity</label>
            <div className="flex flex-wrap gap-1">
              {severityOptions?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => handleSeverityChange(option?.value)}
                  className={`px-2 py-1 text-xs rounded-md nav-transition ${
                    selectedSeverity === option?.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-border'
                  }`}
                >
                  {option?.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label>
            <div className="flex flex-wrap gap-1">
              {categoryOptions?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => handleCategoryChange(option?.value)}
                  className={`px-2 py-1 text-xs rounded-md nav-transition ${
                    selectedCategory === option?.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-border'
                  }`}
                >
                  {option?.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filteredAlerts?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <Icon name="CheckCircle" size={48} className="text-success mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">All Clear</h4>
            <p className="text-sm text-muted-foreground">No alerts matching your filters</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {filteredAlerts?.map((alert) => (
              <div
                key={alert?.id}
                className={`p-3 rounded-lg border cursor-pointer nav-transition hover:shadow-sm ${
                  alert?.isRead ? 'bg-muted/30 border-border' : `${getSeverityBg(alert?.severity)} border-border`
                }`}
                onClick={() => onAlertClick && onAlertClick(alert)}
              >
                <div className="flex items-start space-x-3">
                  <div className={`p-1.5 rounded-md ${getSeverityBg(alert?.severity)}`}>
                    <Icon 
                      name={getSeverityIcon(alert?.severity)} 
                      size={16} 
                      className={getSeverityColor(alert?.severity)} 
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${getSeverityColor(alert?.severity)}`}>
                        {alert?.severity?.toUpperCase()}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatTimestamp(alert?.timestamp)}
                      </span>
                    </div>
                    
                    <h4 className="text-sm font-medium text-foreground mb-1 line-clamp-2">
                      {alert?.title}
                    </h4>
                    
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                      {alert?.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Icon name={getCategoryIcon(alert?.category)} size={12} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground capitalize">{alert?.category}</span>
                        {alert?.location && (
                          <>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground">{alert?.location}</span>
                          </>
                        )}
                      </div>
                      
                      {!alert?.isRead && (
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={(e) => {
                            e?.stopPropagation();
                            onMarkAsRead && onMarkAsRead(alert?.id);
                          }}
                          className="text-xs"
                        >
                          Mark Read
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border">
        <Button variant="outline" size="sm" fullWidth>
          View All Alerts
        </Button>
      </div>
    </div>
  );
};

export default AlertFeed;